import { Injectable, computed, signal } from '@angular/core';

/** `GET back-office/me/permissions`: what the signed-in operator may do. */
export interface MyPermissions {
  /** Role names, for display only. */
  readonly roles: readonly string[];
  /** Effective codes. Already Admin-expanded, so an Admin gets the whole catalogue. */
  readonly permissions: readonly string[];
  /** For an "Administrator" label only. Never use it to decide access. */
  readonly isAdmin: boolean;
  /** Matches the `X-Authz-Fingerprint` header; changes whenever `permissions` does. */
  readonly fingerprint: string;
}

/**
 * The operator's effective permissions, used to show only the menu items and buttons they
 * can use. Hiding a control is a courtesy, not enforcement: the server checks every call,
 * so pages still handle a 403. `PermissionsSync` keeps this up to date.
 */
@Injectable({ providedIn: 'root' })
export class UserPermissions {
  private readonly state = signal<MyPermissions | null>(null);
  private readonly codes = computed(() => new Set(this.state()?.permissions ?? []));

  readonly current = this.state.asReadonly();
  readonly isAdmin = computed(() => this.state()?.isAdmin ?? false);
  readonly fingerprint = computed(() => this.state()?.fingerprint ?? null);

  /** The whole access rule. Don't add `isAdmin ||`: the list is already expanded for Admin. */
  has(code: string): boolean {
    return this.codes().has(code);
  }

  hasAny(codes: readonly string[]): boolean {
    return codes.some((code) => this.has(code));
  }

  set(permissions: MyPermissions): void {
    this.state.set(permissions);
  }

  clear(): void {
    this.state.set(null);
  }
}
